import * as React from "react";
import { useEffect, useRef, useState } from "react";
import useMediaQuery from "../../shared/hooks/useMediaQuery";
import KuikaGrainGradient from "../ui-kit/kuika-grain-gradient/kuika-grain-gradient";
import { AuthenticationBannerSlider } from "./authentication-banner-slider";
import "./authentication-banner.scss";

/**
 * The exact complement of the `max-width: 768px` rule that hides the panel. `min-width:
 * 769px` is NOT that: between the two lies every fractional viewport width, which is
 * routine under Windows display scaling and browser zoom, and there the panel would be
 * visible with no shader behind it.
 */
const BANNER_VISIBLE_QUERY: string = "not all and (max-width: 768px)";

/**
 * The shader needs literal colours — a WebGL uniform cannot read a CSS custom property —
 * but the CSS understudy gradient needs the same values, so the stylesheet stays the
 * single source of truth and these are read back from it at mount. The fallbacks mirror
 * `authentication-banner.scss` and cover jsdom, which does not resolve custom properties
 * from stylesheets.
 */
const SHADER_COLOR_PROPERTIES: readonly string[] = [
  "--kuika-auth-banner-shader-1",
  "--kuika-auth-banner-shader-2",
  "--kuika-auth-banner-shader-3",
  "--kuika-auth-banner-shader-4"
];
const SHADER_COLOR_FALLBACKS: readonly string[] = ["#ffffff", "#246dff", "#246dff", "#ffffff"];

/**
 * The frame the reference composition is posed at. Passing it explicitly rather than
 * starting from 0 means the panel opens on the intended arrangement instead of animating
 * into it, and it is also what a frozen `prefers-reduced-motion` render settles on.
 */
const SHADER_FRAME: number = 2854.5;

const isResolvedColor = (value: string): boolean => {
  return value.startsWith("#") || value.startsWith("rgb") || value.startsWith("hsl");
};

export const AuthenticationBanner: React.FC = () => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [shaderColors, setShaderColors] = useState<readonly string[]>(SHADER_COLOR_FALLBACKS);

  // Rendering the shader only while the panel is on screen matters: ShaderMount's own
  // IntersectionObserver would idle the render loop for a `display: none` panel, but the
  // GL context, program and textures would stay allocated. Unmounting disposes them.
  const isBannerVisible: boolean = useMediaQuery(BANNER_VISIBLE_QUERY);

  useEffect((): void => {
    const element: HTMLDivElement | null = cardRef.current;

    if (!element) {
      return;
    }

    const computed: CSSStyleDeclaration = getComputedStyle(element);
    const resolved: string[] = SHADER_COLOR_PROPERTIES.map((property: string, index: number): string => {
      const value: string = computed.getPropertyValue(property).trim();

      return isResolvedColor(value) ? value : SHADER_COLOR_FALLBACKS[index];
    });

    setShaderColors((current: readonly string[]): readonly string[] => {
      // `#fff` and `#ffffff` are the same colour to the shader, but a new array would
      // still push a uniform update. Nothing here should disturb a running animation.
      const isUnchanged: boolean =
        current.length === resolved.length &&
        current.every((color: string, index: number): boolean => color === resolved[index]);

      return isUnchanged ? current : resolved;
    });
  }, []);

  return (
    <div className="auth-page__right">
      <div className="kuika-auth-banner" ref={cardRef}>
        {isBannerVisible && (
          <KuikaGrainGradient
            colors={shaderColors}
            colorBack="#00000000"
            shape="corners"
            speed={1}
            frame={SHADER_FRAME}
            softness={0.5}
            intensity={0.5}
            noise={0.25}
            maxPixelCount={1500000}
          />
        )}

        <div className="kuika-auth-banner__glass">
          <span className="kuika-auth-banner__eyebrow">All-in-one platform</span>

          <h2 className="kuika-auth-banner__title">
            Design fast,
            <br />
            ship faster
          </h2>

          {/* Everything that changes on a timer lives inside the slider, so a tick never
              re-renders this component — and therefore never touches the shader above,
              whose `frame` is an init-only seed. Note also that the shader's conditional
              and this one are separate expressions in separate parents: neither can
              reorder the other into a different reconciliation slot. */}
          <AuthenticationBannerSlider isVisible={isBannerVisible} />
        </div>
      </div>
    </div>
  );
};
